/* Bundle Builder - Zenwise
   Web component <bundle-builder> — adapted from Arrae BYOB v3.
   No bundle parent; products added individually with _bundle_key property.
*/
if (!customElements.get('bundle-builder')) {
  customElements.define('bundle-builder', class extends HTMLElement {
    constructor() {
      super();
      this.tabs = this.querySelectorAll('[data-bundle-tab]');
      this.collectionGrids = this.querySelectorAll('[data-bundle-collection]');
      this.bundleItemsContainer = this.querySelector('[data-bundle-builder-items]');
      this.subscriptionToggle = this.querySelector('[data-subscription-toggle]');
      this.addToCartBtn = this.querySelector('[data-bundle-add-to-cart]');
      this.itemCountEl = this.querySelector('[data-bundle-builder-item-count-current]');
      this.progressFill = this.querySelector('[data-bundle-progress-fill]');
      this.unlockTextEl = this.querySelector('[data-bundle-unlock-text]');
      this.subtotalEl = this.querySelector('[data-bundle-subtotal]');
      this.savingsEl = this.querySelector('[data-bundle-savings]');
      this.totalEl = this.querySelector('[data-bundle-total]');
      this.ctaPriceEl = this.querySelector('[data-bundle-cta-price]');

      this.minItems = parseInt(this.dataset.bundleBuilderMinItems, 10) || 1;
      this.maxItems = parseInt(this.dataset.bundleBuilderMaxItems, 10) || 8;
      this.addToCartBehavior = this.dataset.addToCartBehavior || 'cart';
      this.subscriptionDiscount = parseFloat(this.dataset.subscriptionDiscount || '15') / 100;
      this.bundleKey = this.dataset.bundleKey || 'byob';
      this.storageKey = 'zenwise-bundle-builder-state';

      this.milestones = JSON.parse(this.querySelector('[data-bundle-milestones-json]')?.textContent || '[]');
      this.bundleItems = [];

      this.init();
    }

    init() {
      this.setupTabs();
      this.setupProducts();
      this.setupBundleSlots();
      this.setupSubscriptionToggle();
      this.setupAddToCart();
      this.setupMobileExpand();
      this.syncFromCart().then(syncedFromCart => {
        if (!syncedFromCart) this.restoreState();
        this.updatePricing();
        this.updateAllProductButtons();
      });
      document.addEventListener('zenwise:cart:closed', () => this.syncFromCart());
      document.addEventListener('zenwise:cart:updated', () => this.syncFromCart());
    }

    async syncFromCart() {
      try {
        const res = await fetch('/cart.js', { credentials: 'same-origin' });
        if (!res.ok) return false;
        const cart = await res.json();
        const byobLines = (cart.items || []).filter(
          item => item.properties && item.properties._bundle_key === this.bundleKey
        );

        if (byobLines.length === 0) {
          // No byob items in cart — clear local state if it had any
          if (this.bundleItems.length > 0) {
            this.bundleItems = [];
            this.renderBundleSlots();
            this.updatePricing();
            this.updateAllProductButtons();
            this.saveState();
          }
          return false;
        }

        this.bundleItems = [];
        let subscriptionCount = 0;
        byobLines.forEach(line => {
          const card = this.querySelector(`[data-bundle-builder-product="${line.product_id}"]`);
          if (!card) return;
          const product = this.getProductFromCard(card);
          if (!product) return;
          const variant = product.variants.find(v => v.id === line.variant_id);
          if (!variant) return;
          const isSubscription = !!line.selling_plan_allocation;
          const byobImage = card.dataset.bundleByobImage || product.featured_image || '';
          for (let i = 0; i < line.quantity; i++) {
            this.bundleItems.push({
              product, variant, isSubscription,
              productId: product.id, byobImage
            });
            if (isSubscription) subscriptionCount++;
          }
        });

        if (this.subscriptionToggle && this.bundleItems.length > 0) {
          this.subscriptionToggle.checked = subscriptionCount > this.bundleItems.length / 2;
          this.updateSubscribeUI();
        }

        this.renderBundleSlots();
        this.updatePricing();
        this.updateAllProductButtons();
        this.saveState();
        return true;
      } catch (err) {
        console.error('Bundle: failed to sync from cart:', err);
        return false;
      }
    }

    setupMobileExpand() {
      const form = this.querySelector('[data-bundle-builder-form]');
      const btn = this.querySelector('[data-bundle-toggle-expand]');
      if (!form || !btn) return;
      form.dataset.mobileExpanded = 'false';
      btn.addEventListener('click', () => {
        const expanded = form.dataset.mobileExpanded === 'true';
        form.dataset.mobileExpanded = expanded ? 'false' : 'true';
        btn.setAttribute('aria-expanded', String(!expanded));
      });
    }

    /* ---------- Tabs ---------- */
    setupTabs() {
      if (!this.tabs.length) return;
      this.tabs.forEach(tab => {
        tab.addEventListener('click', () => this.selectCollection(tab));
      });
    }

    selectCollection(tab) {
      this.tabs.forEach(t => t.classList.remove('active'));
      tab.classList.add('active');
      const handle = tab.dataset.bundleTab;
      this.collectionGrids.forEach(g => {
        g.classList.toggle('hidden', g.dataset.bundleCollection !== handle);
      });
      this.updateAllProductButtons();
    }

    /* ---------- Products ---------- */
    setupProducts() {
      this.addEventListener('click', e => {
        const addBtn = e.target.closest('[data-bundle-builder-add]');
        if (addBtn && !addBtn.classList.contains('bundle-builder__add-btn--with-quantity')) {
          e.preventDefault();
          const card = addBtn.closest('[data-bundle-builder-product]');
          if (card) this.addProductToBundle(card);
          return;
        }
        const inc = e.target.closest('[data-bundle-qty-inc]');
        if (inc) {
          e.preventDefault();
          const card = inc.closest('[data-bundle-builder-product]');
          if (card) this.incrementProductQuantity(card);
          return;
        }
        const dec = e.target.closest('[data-bundle-qty-dec]');
        if (dec) {
          e.preventDefault();
          const card = dec.closest('[data-bundle-builder-product]');
          if (card) this.decrementProductQuantity(card);
        }
      });
    }

    getProductFromCard(card) {
      try {
        const json = card.querySelector('[data-bundle-builder-product-json]');
        return json ? JSON.parse(json.textContent) : null;
      } catch (err) {
        console.error('Bundle: invalid product JSON', err);
        return null;
      }
    }

    addProductToBundle(card) {
      const product = this.getProductFromCard(card);
      if (!product) return;
      if (this.bundleItems.length >= this.maxItems) {
        alert(`Maximum ${this.maxItems} products allowed in bundle`);
        return;
      }
      const isSub = this.subscriptionToggle?.checked || false;
      const variant = this.getVariantForPurchaseType(product, isSub);
      if (!variant) return;
      const byobImage = card.dataset.bundleByobImage || product.featured_image || '';
      this.bundleItems.push({ product, variant, isSubscription: isSub, productId: product.id, byobImage });
      this.afterBundleChange(card);
    }

    incrementProductQuantity(card) {
      this.addProductToBundle(card);
    }

    decrementProductQuantity(card) {
      const product = this.getProductFromCard(card);
      if (!product) return;
      const idx = this.bundleItems.findIndex(i => i.productId === product.id);
      if (idx === -1) return;
      this.bundleItems.splice(idx, 1);
      this.afterBundleChange(card);
    }

    afterBundleChange(card) {
      const product = this.getProductFromCard(card);
      const qty = product ? this.bundleItems.filter(i => i.productId === product.id).length : 0;
      if (card) this.updateProductButton(card, qty);
      this.renderBundleSlots();
      this.updatePricing();
      this.saveState();
    }

    updateAllProductButtons() {
      this.querySelectorAll('[data-bundle-builder-product]').forEach(card => {
        const product = this.getProductFromCard(card);
        if (!product) return;
        const qty = this.bundleItems.filter(i => i.productId === product.id).length;
        this.updateProductButton(card, qty);
      });
    }

    updateProductButton(card, qty) {
      const btn = card.querySelector('[data-bundle-builder-add]');
      if (!btn) return;
      if (qty > 0) {
        btn.classList.add('bundle-builder__add-btn--with-quantity');
        btn.innerHTML = `
          <button type="button" class="bundle-builder__qty-btn" data-bundle-qty-dec aria-label="Decrease">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2.5 7H11.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>
          </button>
          <span class="bundle-builder__qty-number">${qty}</span>
          <button type="button" class="bundle-builder__qty-btn" data-bundle-qty-inc aria-label="Increase">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 2.5V11.5M2.5 7H11.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>
          </button>
        `;
      } else {
        btn.classList.remove('bundle-builder__add-btn--with-quantity');
        btn.textContent = 'ADD +';
      }
    }

    /* ---------- Variant selection (subscription-aware) ---------- */
    getVariantForPurchaseType(product, isSubscription) {
      if (!product.variants?.length) return null;
      if (isSubscription) {
        const subVariant = product.variants.find(v => {
          if (!v.available) return false;
          const hasMonthlyPlan = v.selling_plan_allocations?.some(a => {
            const name = (a.selling_plan_group_name || '').toLowerCase();
            return name.includes('1 month') || name.includes('monthly') || name.includes('month');
          });
          const titleMatches = /1 month|month|subscription/i.test(v.title);
          return hasMonthlyPlan || titleMatches;
        });
        if (subVariant) return subVariant;
      }
      const otp = product.variants.find(v => v.available && /one[- ]?time|otp|1 time/i.test(v.title));
      return otp || product.variants.find(v => v.available) || product.variants[0];
    }

    getSellingPlanId(variant) {
      if (!variant?.selling_plan_allocations?.length) return null;
      const monthly = variant.selling_plan_allocations.find(a => {
        const name = (a.selling_plan_group_name || '').toLowerCase();
        return name.includes('1 month') || name.includes('monthly');
      });
      return (monthly || variant.selling_plan_allocations[0]).selling_plan_id;
    }

    /* ---------- Slots ---------- */
    setupBundleSlots() {
      this.bundleItemsContainer.addEventListener('click', e => {
        const removeBtn = e.target.closest('[data-bundle-builder-item-remove]');
        if (!removeBtn) return;
        e.preventDefault();
        const slot = removeBtn.closest('[data-bundle-builder-item]');
        const idx = parseInt(slot.dataset.bundleBuilderItem, 10) - 1;
        this.removeProductFromSlot(idx);
      });
    }

    removeProductFromSlot(index) {
      if (index < 0 || index >= this.bundleItems.length) return;
      const removed = this.bundleItems[index];
      this.bundleItems.splice(index, 1);
      if (removed?.productId) {
        const card = this.querySelector(`[data-bundle-builder-product="${removed.productId}"]`);
        if (card) {
          const newQty = this.bundleItems.filter(i => i.productId === removed.productId).length;
          this.updateProductButton(card, newQty);
        }
      }
      this.renderBundleSlots();
      this.updatePricing();
      this.saveState();
    }

    renderBundleSlots() {
      const container = this.bundleItemsContainer;
      const desired = this.bundleItems.length + 1;
      const visibleSlots = Math.min(this.maxItems, Math.max(this.minItems, desired));

      // Ensure enough slot elements exist
      let existing = container.querySelectorAll('[data-bundle-builder-item]');
      while (existing.length < visibleSlots) {
        container.insertAdjacentHTML('beforeend', this.emptySlotHTML(existing.length + 1));
        existing = container.querySelectorAll('[data-bundle-builder-item]');
      }

      existing.forEach((slot, i) => {
        const slotNumber = i + 1;
        if (slotNumber > visibleSlots) { slot.style.display = 'none'; return; }
        slot.style.display = '';
        slot.dataset.bundleBuilderItem = String(slotNumber);
        const placeholder = slot.querySelector('.bundle-builder__item-placeholder');
        const content = slot.querySelector('[data-bundle-item-content]');
        const addText = slot.querySelector('[data-bundle-item-add-text]');
        const item = this.bundleItems[i];

        if (item) {
          slot.classList.remove('bundle-builder__item--empty');
          slot.classList.add('bundle-builder__item--filled');
          if (placeholder) placeholder.style.display = 'none';
          if (addText) addText.style.display = 'none';
          if (content) {
            content.style.display = 'flex';
            const img = content.querySelector('[data-bundle-item-image]');
            const title = content.querySelector('[data-bundle-item-title]');
            const price = slot.querySelector('[data-bundle-item-price]');
            const slotImageSrc = item.byobImage || item.product.featured_image;
            if (img && slotImageSrc) {
              img.innerHTML = `<img src="${slotImageSrc}" alt="${this.escape(item.product.title)}" loading="lazy">`;
            }
            if (title) title.textContent = item.product.title;
            if (price) price.textContent = this.formatMoney(this.getItemPrice(item));
          }
        } else {
          slot.classList.add('bundle-builder__item--empty');
          slot.classList.remove('bundle-builder__item--filled');
          if (placeholder) placeholder.style.display = '';
          if (content) content.style.display = 'none';
          if (addText) {
            addText.style.display = '';
            const subtotal = this.calculateSubtotal();
            const next = this.getNextMilestone(subtotal);
            if (next) {
              const needed = next.threshold - subtotal;
              addText.textContent = `${this.formatMoney(needed)} to unlock ${next.label}`;
            } else {
              addText.textContent = 'Select Product';
            }
          }
        }
      });

      if (this.itemCountEl) this.itemCountEl.textContent = this.bundleItems.length;
      const itemCountLabelEl = this.querySelector('[data-bundle-builder-item-count-label]');
      if (itemCountLabelEl) itemCountLabelEl.textContent = this.bundleItems.length === 1 ? 'Item' : 'Items';
    }

    emptySlotHTML(n) {
      return `
        <div class="bundle-builder__item bundle-builder__item--empty" data-bundle-builder-item="${n}">
          <div class="bundle-builder__item-placeholder">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M10 4V16M4 10H16" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
          </div>
          <div class="bundle-builder__item-content" data-bundle-item-content>
            <div class="bundle-builder__item-image" data-bundle-item-image></div>
            <div class="bundle-builder__item-details">
              <p class="bundle-builder__item-title" data-bundle-item-title></p>
            </div>
            <p class="bundle-builder__item-price" data-bundle-item-price></p>
            <button type="button" class="bundle-builder__item-remove" data-bundle-builder-item-remove aria-label="Remove">
              <svg width="16" height="18" viewBox="0 0 16 18" fill="none" aria-hidden="true"><path d="M2 4.5h12M6.5 8v6M9.5 8v6M3.5 4.5L4 15a1 1 0 001 1h6a1 1 0 001-1l.5-10.5M6 4.5V2.5a1 1 0 011-1h2a1 1 0 011 1v2" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
          </div>
          <span class="bundle-builder__item-add-text" data-bundle-item-add-text>Select Product</span>
        </div>`;
    }

    /* ---------- Subscribe toggle ---------- */
    setupSubscriptionToggle() {
      if (!this.subscriptionToggle) return;
      this.subscriptionToggle.addEventListener('change', () => {
        const isSub = this.subscriptionToggle.checked;
        this.bundleItems = this.bundleItems.map(item => ({
          ...item,
          isSubscription: isSub,
          variant: this.getVariantForPurchaseType(item.product, isSub)
        }));
        this.updateSubscribeUI();
        this.renderBundleSlots();
        this.updatePricing();
        this.saveState();
      });
      this.updateSubscribeUI();
    }

    updateSubscribeUI() {
      const sub = this.querySelector('[data-subscription-benefits]');
      const isOn = !!this.subscriptionToggle?.checked;
      if (sub) sub.classList.toggle('collapsed', !isOn);
      const subLabel = this.querySelector('[data-purchase-type-label-subscribe]');
      const otpLabel = this.querySelector('[data-purchase-type-label-otp]');
      if (subLabel) subLabel.hidden = !isOn;
      if (otpLabel) otpLabel.hidden = isOn;
      this.updateSubscriptionSaveLabel();
    }

    updateSubscriptionSaveLabel() {
      const labelEl = this.querySelector('[data-subscription-save-label]');
      if (!labelEl) return;
      const manual = (labelEl.dataset.subscriptionSaveManual || '').trim();
      if (manual) {
        labelEl.textContent = manual;
        return;
      }
      const pct = this.calculateSubscriptionDiscountPct();
      if (pct > 0) labelEl.textContent = `Save ${pct}%`;
    }

    calculateSubscriptionDiscountPct() {
      const samples = this.bundleItems.length
        ? this.bundleItems.map(i => i.product)
        : this.collectSampleProducts();
      let otpTotal = 0;
      let subTotal = 0;
      samples.forEach(product => {
        const otpVariant = this.getVariantForPurchaseType(product, false);
        const subVariant = this.getVariantForPurchaseType(product, true);
        if (!otpVariant || !subVariant) return;
        const otpPrice = otpVariant.price;
        const subPrice = subVariant.selling_plan_allocations?.[0]?.price
          ?? Math.round(subVariant.price * (1 - this.subscriptionDiscount));
        if (otpPrice > 0 && subPrice < otpPrice) {
          otpTotal += otpPrice;
          subTotal += subPrice;
        }
      });
      if (otpTotal === 0) return Math.round(this.subscriptionDiscount * 100);
      return Math.round(((otpTotal - subTotal) / otpTotal) * 100);
    }

    collectSampleProducts() {
      const products = [];
      const seen = new Set();
      this.querySelectorAll('[data-bundle-builder-product]').forEach(card => {
        const p = this.getProductFromCard(card);
        if (p && !seen.has(p.id)) { seen.add(p.id); products.push(p); }
      });
      return products;
    }

    /* ---------- Pricing ---------- */
    getItemPrice(item) {
      if (item.isSubscription && item.variant.selling_plan_allocations?.length > 0) {
        return item.variant.selling_plan_allocations[0].price;
      }
      if (item.isSubscription) {
        return Math.round(item.variant.price * (1 - this.subscriptionDiscount));
      }
      return item.variant.price;
    }

    calculateSubtotal() {
      return this.bundleItems.reduce((s, i) => s + this.getItemPrice(i), 0);
    }

    calculateSavings() {
      const original = this.bundleItems.reduce((s, i) => s + i.variant.price, 0);
      const subtotal = this.calculateSubtotal();
      const milestone = this.getCurrentMilestone(subtotal);
      const milestoneDiscount = milestone?.discount || 0;
      return (original - subtotal) + milestoneDiscount;
    }

    getCurrentMilestone(price) {
      let current = null;
      for (const m of this.milestones) {
        if (price >= m.threshold) current = m; else break;
      }
      return current;
    }

    getNextMilestone(price) {
      return this.milestones.find(m => price < m.threshold) || null;
    }

    updatePricing() {
      const subtotal = this.calculateSubtotal();
      const savings = this.calculateSavings();
      const milestone = this.getCurrentMilestone(subtotal);
      const milestoneDiscount = milestone?.discount || 0;
      const total = Math.max(0, subtotal - milestoneDiscount);

      if (this.subtotalEl) this.subtotalEl.textContent = this.formatMoney(subtotal);
      if (this.totalEl) this.totalEl.textContent = this.formatMoney(total);
      if (this.ctaPriceEl) this.ctaPriceEl.textContent = this.formatMoney(total);
      if (this.savingsEl) {
        this.savingsEl.textContent = this.formatMoney(savings);
        const row = this.savingsEl.closest('.bundle-builder__price-row');
        if (row) row.style.display = savings > 0 ? '' : 'none';
      }

      // Progress + milestones
      const next = this.getNextMilestone(subtotal);
      if (this.unlockTextEl) {
        if (next) {
          const needed = next.threshold - subtotal;
          this.unlockTextEl.innerHTML = `Spend <strong>${this.formatMoney(needed)}</strong> more to unlock <strong>${next.label}</strong>`;
        } else if (this.milestones.length) {
          this.unlockTextEl.textContent = "You've unlocked all discounts!";
        } else {
          this.unlockTextEl.textContent = '';
        }
      }

      // Progress fill: uses fixed milestone positions [30%, 65%, 100%]
      const positions = [30, 65, 100];
      if (this.progressFill && this.milestones.length > 0) {
        let pct = 0;
        if (!next) {
          pct = 100;
        } else {
          const nextIdx = this.milestones.indexOf(next);
          const prevIdx = nextIdx - 1;
          const prevPos = prevIdx >= 0 ? positions[prevIdx] : 0;
          const nextPos = positions[nextIdx];
          const prevThreshold = prevIdx >= 0 ? this.milestones[prevIdx].threshold : 0;
          const range = next.threshold - prevThreshold;
          const within = range > 0 ? (subtotal - prevThreshold) / range : 0;
          pct = prevPos + Math.max(0, Math.min(1, within)) * (nextPos - prevPos);
        }
        this.progressFill.style.width = `${Math.max(0, Math.min(100, pct))}%`;
      }

      this.querySelectorAll('[data-milestone-step]').forEach(el => {
        const step = parseInt(el.dataset.milestoneStep, 10);
        const m = this.milestones[step - 1];
        if (!m) return;
        el.classList.toggle('active', subtotal >= m.threshold);
      });

      this.updateShippingDisplay(subtotal);
      this.updateAddToCartButton();
    }

    updateShippingDisplay(subtotal) {
      const el = this.querySelector('[data-bundle-shipping]');
      if (!el) return;
      const shippingMilestone = this.milestones[0];
      if (!shippingMilestone) return;
      const baseShipping = parseInt(el.dataset.baseShipping || el.textContent.replace(/[^0-9]/g, '') || '0', 10);
      if (!el.dataset.baseShipping) el.dataset.baseShipping = String(baseShipping);
      const formatted = this.formatMoney(baseShipping);
      const isSub = !!this.subscriptionToggle?.checked;
      const qualifies = subtotal >= shippingMilestone.threshold || (isSub && shippingMilestone.threshold === 0);
      if (qualifies) {
        el.innerHTML = `<span class="bundle-builder__strike">${formatted}</span>FREE`;
      } else {
        el.textContent = formatted;
      }
    }

    updateAddToCartButton() {
      if (!this.addToCartBtn) return;
      const enabled = this.bundleItems.length >= this.minItems;
      this.addToCartBtn.disabled = !enabled;
      this.addToCartBtn.classList.toggle('disabled', !enabled);
    }

    /* ---------- Add to cart ---------- */
    setupAddToCart() {
      if (!this.addToCartBtn) return;
      this.addToCartBtn.addEventListener('click', () => this.addBundleToCart());
    }

    async addBundleToCart() {
      if (this.bundleItems.length < this.minItems) {
        alert(`Please add at least ${this.minItems} product${this.minItems > 1 ? 's' : ''} to your bundle`);
        return;
      }
      this.setLoading(true);

      // Group by variant + selling plan (so same product+plan adds quantity)
      const grouped = new Map();
      this.bundleItems.forEach(item => {
        const sellingPlan = item.isSubscription ? this.getSellingPlanId(item.variant) : null;
        const key = `${item.variant.id}_${sellingPlan || 'otp'}`;
        if (grouped.has(key)) {
          grouped.get(key).quantity += 1;
        } else {
          const entry = {
            id: item.variant.id,
            quantity: 1,
            properties: { _bundle_key: this.bundleKey }
          };
          if (sellingPlan) entry.selling_plan = sellingPlan;
          grouped.set(key, entry);
        }
      });

      const items = Array.from(grouped.values());

      try {
        // 1) Remove any existing byob lines from the cart so the BYOB sidebar
        //    is the source of truth (avoids quantity duplication).
        const cartRes = await fetch('/cart.js', { credentials: 'same-origin' });
        if (cartRes.ok) {
          const cart = await cartRes.json();
          const updates = {};
          (cart.items || []).forEach(item => {
            if (item.properties && item.properties._bundle_key === this.bundleKey) {
              updates[item.key] = 0;
            }
          });
          if (Object.keys(updates).length) {
            await fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
              body: JSON.stringify({ updates }),
              credentials: 'same-origin'
            });
          }
        }

        // 2) Add the current bundle items
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items, attributes: { _source_page: 'BYOB' } }),
          credentials: 'same-origin'
        });
        if (!res.ok) throw new Error('Failed to add bundle to cart');
        await res.json();
        // Only clear bundle state if all milestones were unlocked.
        // Otherwise keep the items so the user can keep adding to the same bundle.
        const subtotal = this.calculateSubtotal();
        const allMilestonesUnlocked = this.milestones.length > 0 &&
          subtotal >= this.milestones[this.milestones.length - 1].threshold;
        if (allMilestonesUnlocked) this.clearState();
        if (this.addToCartBehavior === 'checkout') {
          window.location.href = '/checkout';
        } else {
          document.dispatchEvent(new CustomEvent('zenwise:cart:added'));
          if (typeof window.openCartDrawer === 'function') window.openCartDrawer();
          this.setLoading(false);
        }
      } catch (err) {
        console.error('Bundle add to cart error:', err);
        alert('There was an error adding your bundle to cart. Please try again.');
        this.setLoading(false);
      }
    }

    setLoading(loading) {
      if (!this.addToCartBtn) return;
      this.addToCartBtn.classList.toggle('loading', loading);
      this.addToCartBtn.disabled = loading || this.bundleItems.length < this.minItems;
    }

    /* ---------- State persistence ---------- */
    saveState() {
      try {
        localStorage.setItem(this.storageKey, JSON.stringify({
          items: this.bundleItems.map(i => ({
            productId: i.product.id,
            variantId: i.variant.id,
            isSubscription: i.isSubscription
          })),
          isSubscription: this.subscriptionToggle?.checked || false,
          ts: Date.now()
        }));
      } catch (err) { /* noop */ }
    }

    restoreState() {
      try {
        const raw = localStorage.getItem(this.storageKey);
        if (!raw) return;
        const state = JSON.parse(raw);
        if (Date.now() - state.ts > 24 * 60 * 60 * 1000) {
          localStorage.removeItem(this.storageKey);
          return;
        }
        if (this.subscriptionToggle && typeof state.isSubscription === 'boolean') {
          this.subscriptionToggle.checked = state.isSubscription;
        }
        state.items?.forEach(saved => {
          const card = this.querySelector(`[data-bundle-builder-product="${saved.productId}"]`);
          if (!card) return;
          const product = this.getProductFromCard(card);
          if (!product) return;
          const variant = product.variants.find(v => v.id === saved.variantId) || this.getVariantForPurchaseType(product, saved.isSubscription);
          const byobImage = card.dataset.bundleByobImage || product.featured_image || '';
          this.bundleItems.push({ product, variant, isSubscription: saved.isSubscription, productId: product.id, byobImage });
        });
        this.updateSubscribeUI();
        this.renderBundleSlots();
      } catch (err) {
        localStorage.removeItem(this.storageKey);
      }
    }

    clearState() {
      try { localStorage.removeItem(this.storageKey); } catch (err) { /* noop */ }
      this.bundleItems = [];
      this.renderBundleSlots();
      this.updatePricing();
      this.updateAllProductButtons();
    }

    /* ---------- Helpers ---------- */
    formatMoney(cents) {
      if (typeof window.Shopify !== 'undefined' && window.Shopify.formatMoney) {
        const fmt = window.theme?.moneyFormat || '${{amount}}';
        return window.Shopify.formatMoney(cents, fmt);
      }
      return `$${(cents / 100).toFixed(2)}`;
    }

    escape(str) {
      return String(str).replace(/[&<>"']/g, s => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[s]);
    }
  });
}
